/**
 * 媒體分組邏輯工具
 * 用於按流程實例和流程變量分組媒體文件
 */

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];

/**
 * 檢查文件是否為圖片文件
 * @param {string} filePath - 文件路徑
 * @returns {boolean} 是否為圖片文件
 */
export const isImageFile = (filePath) => {
  if (!filePath) return false;
  const extension = filePath.toLowerCase().substring(filePath.lastIndexOf('.'));
  return IMAGE_EXTENSIONS.includes(extension);
};

/**
 * 獲取流程實例列表
 * @param {Object} filters - 過濾條件
 * @returns {Promise<Array>} 流程實例列表
 */
export const fetchWorkflowExecutions = async (filters = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('請先登入');
    }

    const params = new URLSearchParams();
    
    // 添加分頁參數（獲取所有記錄，設置較大的頁面大小）
    params.append('page', '1');
    params.append('pageSize', '1000'); // 獲取最多 1000 條記錄
    
    // 添加過濾條件
    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    
    if (filters.workflowName) {
      params.append('search', filters.workflowName);
    }
    
    if (filters.startDateFrom) {
      const from = filters.startDateFrom;
      params.append('startDateFrom', typeof from === 'string' ? from : from.toISOString());
    }
    
    if (filters.startDateTo) {
      const to = filters.startDateTo;
      params.append('startDateTo', typeof to === 'string' ? to : to.toISOString());
    }

    // 支援 startDateRange: [from, to] 格式（InstanceFilters 實際傳入的格式）
    if (filters.startDateRange && Array.isArray(filters.startDateRange) && filters.startDateRange.length === 2) {
      const [from, to] = filters.startDateRange;
      if (from) params.append('startDateFrom', typeof from === 'string' ? from : from.toISOString());
      if (to)   params.append('startDateTo',   typeof to   === 'string' ? to   : to.toISOString());
    }

    const response = await fetch(`/api/workflowexecutions/monitor?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('獲取流程實例列表失敗:', error);
    throw error;
  }
};

/**
 * 獲取流程實例的媒體文件
 * @param {number} executionId - 流程實例 ID
 * @returns {Promise<Array>} 媒體文件列表
 */
export const fetchExecutionMediaFiles = async (executionId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('請先登入');
    }

    const response = await fetch(`/api/workflowexecutions/${executionId}/media-files`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return []; // 沒有媒體文件，返回空數組
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    const mediaFiles = result.data || [];
    
    // 過濾出圖片文件
    return mediaFiles.filter(file => isImageFile(file.filePath || file.fileName));
  } catch (error) {
    console.error(`獲取流程實例 ${executionId} 的媒體文件失敗:`, error);
    return []; // 出錯時返回空數組，不影響其他實例
  }
};

/**
 * 獲取流程實例關聯的 eForm 實例列表
 * @param {number} executionId - 流程實例 ID
 * @returns {Promise<Array>} eForm 實例列表
 */
export const fetchExecutionEformInstances = async (executionId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('請先登入');

    const response = await fetch(`/api/workflowexecutions/${executionId}/eform-instances`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return Array.isArray(result) ? result : (result.data || []);
  } catch (error) {
    console.error(`獲取流程實例 ${executionId} 的 eForm 實例失敗:`, error);
    return [];
  }
};

/**
 * 取得單一 eForm 實例的完整資料（含 parsedFieldValues）
 * /api/workflowexecutions/{id}/eform-instances 只回傳輕量資料，
 * 需要再呼叫 /api/eforminstances/{id} 取得實際填寫值。
 * @param {number} instanceId - eForm 實例 ID
 * @returns {Promise<Object|null>}
 */
export const fetchEformInstanceFullData = async (instanceId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token || !instanceId) return null;
    const response = await fetch(`/api/eforminstances/${instanceId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return null;
    const data = await response.json();
    return {
      ...data,
      formName: data.formName || data.form_name,
      parsedFieldValues: data.parsedFieldValues ?? data.parsed_field_values ?? {},
    };
  } catch (error) {
    console.error(`獲取 eForm 實例 ${instanceId} 完整資料失敗:`, error);
    return null;
  }
};

/**
 * 從 eForm 實例列表中提取指定欄位的分組值
 * @param {Array} eformInstances - eForm 實例列表（需含 parsedFieldValues）
 * @param {Array} groupingEformFields - 要分組的 eForm 欄位 [{ eformName, fieldId, displayLabel }]
 * @returns {Object} { "eformName:fieldId": value } 格式的值字典
 */
export const extractEformGroupValues = (eformInstances, groupingEformFields = []) => {
  const values = {};
  if (!groupingEformFields || groupingEformFields.length === 0) return values;

  for (const { eformName, fieldId } of groupingEformFields) {
    const key = `${eformName}:${fieldId}`;

    // 以 formName 匹配（和 fieldDisplaySettings 中的 formName 一致）
    const matchingInstances = eformInstances.filter(
      inst => (inst.formName || inst.form_name) === eformName
    );

    if (matchingInstances.length === 0) {
      values[key] = null;
      continue;
    }

    // 若同一 eForm 定義有多筆實例，取所有非空值並以 '; ' 合併
    const fieldValues = matchingInstances
      .map(inst => {
        const parsed = inst.parsedFieldValues || inst.parsed_field_values || {};
        const v = parsed[fieldId];
        return v !== undefined && v !== null && String(v).trim() !== '' ? String(v) : null;
      })
      .filter(v => v !== null);

    values[key] = fieldValues.length > 0 ? fieldValues.join('; ') : null;
  }

  return values;
};

/**
 * 獲取流程實例的流程變量值
 * @param {number} executionId - 流程實例 ID
 * @returns {Promise<Object>} 流程變量值字典 { variableName: value }
 */
export const fetchExecutionProcessVariables = async (executionId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('請先登入');
    }

    const response = await fetch(`/api/processvariables/values/dictionary/${executionId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return {}; // 沒有流程變量，返回空對象
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data || {};
  } catch (error) {
    console.error(`獲取流程實例 ${executionId} 的流程變量值失敗:`, error);
    return {}; // 出錯時返回空對象
  }
};

/**
 * 檢查流程變量值是否符合過濾條件
 * @param {*} pvValue - 流程變量值
 * @param {string} operator - 運算子 (equals, contains, greater_than, less_than, not_equals)
 * @param {string} filterValue - 過濾值
 * @returns {boolean} 是否符合條件
 */
const matchesFilter = (pvValue, operator, filterValue) => {
  if (pvValue === null || pvValue === undefined) {
    return false; // 如果流程變量值為空，不符合任何條件
  }

  // JSON 物件/陣列用 JSON.stringify 轉字串，以便 contains 可搜尋內部文字
  const pvValueStr = (typeof pvValue === 'object'
    ? JSON.stringify(pvValue)
    : String(pvValue)
  ).toLowerCase();
  const filterValueStr = String(filterValue).toLowerCase();

  switch (operator) {
    case 'equals':
      return pvValueStr === filterValueStr;
    case 'contains':
      return pvValueStr.includes(filterValueStr);
    case 'greater_than':
      // 嘗試數字比較
      const numPv = Number(pvValue);
      const numFilter = Number(filterValue);
      if (!isNaN(numPv) && !isNaN(numFilter)) {
        return numPv > numFilter;
      }
      // 如果不是數字，使用字符串比較
      return pvValueStr > filterValueStr;
    case 'less_than':
      // 嘗試數字比較
      const numPv2 = Number(pvValue);
      const numFilter2 = Number(filterValue);
      if (!isNaN(numPv2) && !isNaN(numFilter2)) {
        return numPv2 < numFilter2;
      }
      // 如果不是數字，使用字符串比較
      return pvValueStr < filterValueStr;
    case 'not_equals':
      return pvValueStr !== filterValueStr;
    default:
      return false;
  }
};

/**
 * 檢查流程實例是否符合所有過濾條件
 * @param {Object} processVariables - 流程變量值字典
 * @param {Array} pvFilters - 流程變量過濾條件 [{ variableName, operator, value }]
 * @returns {boolean} 是否符合所有過濾條件
 */
const matchesAllFilters = (processVariables, pvFilters = []) => {
  if (!pvFilters || pvFilters.length === 0) {
    return true; // 沒有過濾條件，所有實例都符合
  }

  // 檢查所有過濾條件是否都符合（AND 邏輯）
  return pvFilters.every(filter => {
    if (!filter.variableName || !filter.operator || filter.value === undefined || filter.value === '') {
      return true; // 如果過濾條件不完整，跳過
    }
    
    const pvValue = processVariables[filter.variableName];
    return matchesFilter(pvValue, filter.operator, filter.value);
  });
};

/**
 * 標準化狀態顯示（根據後端過濾邏輯）
 * 將多個相關狀態統一顯示為標準狀態
 * @param {string} status - 原始狀態
 * @returns {string} 標準化後的狀態顯示
 */
export const normalizeStatusForDisplay = (status) => {
  if (!status) return 'N/A';
  
  const statusLower = status.toLowerCase();
  
  // running: 包含 "run" 或 "wait" → 顯示為 "Running"
  if (statusLower.includes('run') || statusLower.includes('wait')) {
    return 'Running';
  }
  
  // completed: 包含 "complete" → 顯示為 "Completed"
  if (statusLower.includes('complete')) {
    return 'Completed';
  }
  
  // failed: 包含 "fail" → 顯示為 "Failed"
  if (statusLower.includes('fail')) {
    return 'Failed';
  }
  
  // 其他狀態保持原樣
  return status;
};

/**
 * 根據選中的分組流程變量及 eForm 欄位，生成分組鍵
 * @param {Object} processVariables - 流程變量值字典 { variableName: value }
 * @param {Array} groupingPvs - 用於分組的流程變量名稱列表
 * @param {Object} eformValues - eForm 欄位值字典 { "eformId:fieldName": value }
 * @param {Array} groupingEformFields - 用於分組的 eForm 欄位 [{ eformId, fieldName, ... }]
 * @returns {string} 分組鍵
 */
export const generateGroupKey = (processVariables, groupingPvs = [], eformValues = {}, groupingEformFields = []) => {
  const hasPvGrouping = groupingPvs && groupingPvs.length > 0;
  const hasEformGrouping = groupingEformFields && groupingEformFields.length > 0;

  if (!hasPvGrouping && !hasEformGrouping) {
    return 'all';
  }

  const parts = [];

  // PV 分組部分
  if (hasPvGrouping) {
    const pvKeyParts = groupingPvs
      .map(variableName => {
        const pvValue = processVariables[variableName];
        const valueStr = pvValue !== null && pvValue !== undefined
          ? String(pvValue).trim().replace(/\s+/g, ' ')
          : 'null';
        return `${variableName}_${valueStr}`;
      })
      .sort()
      .join('|');
    parts.push(`pv:${pvKeyParts}`);
  }

  // eForm 欄位分組部分
  if (hasEformGrouping) {
    const eformKeyParts = groupingEformFields
      .map(({ eformName, fieldId }) => {
        const key = `${eformName}:${fieldId}`;
        const value = eformValues[key];
        // 正規化：trim 並將換行/多餘空白替換成單個空格，避免因換行符導致相同語意值被分到不同組
        const valueStr = value !== null && value !== undefined
          ? String(value).trim().replace(/\s+/g, ' ')
          : 'null';
        return `${key}=${valueStr}`;
      })
      .sort()
      .join('|');
    parts.push(`ef:${eformKeyParts}`);
  }

  return parts.join('__');
};

/**
 * 按流程實例和流程變量分組媒體文件
 * @param {Object} instanceFilters - 實例過濾條件
 * @param {Array} pvFilters - 流程變量過濾條件 [{ variableName, operator, value }]
 * @param {Array} groupingPvs - 用於分組的流程變量名稱列表
 * @param {Function} onProgress - 進度回調函數 (current, total)
 * @param {Array} groupingEformFields - 用於分組的 eForm 欄位 [{ eformId, eformName, fieldName, fieldLabel }]
 * @returns {Promise<Array>} 分組後的媒體數據
 */
export const groupMediaByExecutionAndPV = async (instanceFilters, pvFilters = [], groupingPvs = [], onProgress = null, groupingEformFields = []) => {
  const hasEformGrouping = Array.isArray(groupingEformFields) && groupingEformFields.length > 0;
  // 'eform'：用 eForm 最早 createdAt 篩選；'process'（預設）：用 execution startedAt（後端篩選）
  const dateFilterType = instanceFilters.dateFilterType || 'process';
  // 當 eform 模式時，不把日期傳給後端（改前端過濾），其他條件照傳
  const apiFilters = dateFilterType === 'eform'
    ? { ...instanceFilters, startDateRange: null, startDateFrom: null, startDateTo: null }
    : instanceFilters;
  // eform 模式下需要 eForm instances（即使不做欄位分組）以取得時間
  const needEformInstances = hasEformGrouping || dateFilterType === 'eform';

  try {
    // 1. 獲取符合條件的流程實例列表
    console.log('[MediaGrouping] 開始獲取流程實例列表，過濾條件:', instanceFilters, 'dateFilterType:', dateFilterType);
    console.log('[MediaGrouping] pvFilters=', JSON.stringify(pvFilters), 'groupingPvs=', groupingPvs, 'groupingEformFields=', groupingEformFields);
    const executions = await fetchWorkflowExecutions(apiFilters);
    console.log('[MediaGrouping] 獲取到', executions.length, '個流程實例, IDs:', executions.map(e => e.id).join(','));

    if (executions.length === 0) {
      return [];
    }

    // 2. 對每個實例並行獲取媒體文件、流程變量（及 eForm 實例）
    const total = executions.length;
    let processed = 0;
    
    const executionData = await Promise.all(
      executions.map(async (execution) => {
        try {
          const fetchTasks = [
            fetchExecutionMediaFiles(execution.id),
            fetchExecutionProcessVariables(execution.id),
          ];
          if (needEformInstances) {
            fetchTasks.push(fetchExecutionEformInstances(execution.id));
          }

          const results = await Promise.all(fetchTasks);
          const mediaFiles = results[0];
          const processVariables = results[1];
          let eformInstances = needEformInstances ? (results[2] || []) : [];

          // eform-instances 端點只回傳輕量資料（無 parsedFieldValues）
          // 需對符合分組欄位的實例再取完整資料（僅 grouping 需要，時間篩選用輕量即可）
          if (hasEformGrouping && eformInstances.length > 0) {
            const relevantNames = new Set(groupingEformFields.map(f => f.eformName));
            const relevantInstances = eformInstances.filter(inst =>
              relevantNames.has(inst.formName || inst.form_name)
            );

            if (relevantInstances.length > 0) {
              const fullDataList = await Promise.all(
                relevantInstances.map(inst => fetchEformInstanceFullData(inst.id))
              );
              // 建立 instanceId → fullData 映射，替換輕量資料
              const fullDataById = {};
              fullDataList.filter(Boolean).forEach(d => { fullDataById[d.id] = d; });
              eformInstances = eformInstances.map(inst => fullDataById[inst.id] || inst);
            }
          }

          processed++;
          if (onProgress) onProgress(processed, total);

          return { execution, mediaFiles, processVariables, eformInstances };
        } catch (error) {
          console.error(`處理流程實例 ${execution.id} 失敗:`, error);
          processed++;
          if (onProgress) onProgress(processed, total);
          return { execution, mediaFiles: [], processVariables: {}, eformInstances: [] };
        }
      })
    );

    // 3. 若 dateFilterType === 'eform' 且有日期範圍，用 eForm 最早 createdAt 前端過濾
    let filteredData = executionData;
    if (dateFilterType === 'eform' && instanceFilters.startDateRange?.length === 2) {
      const [fromStr, toStr] = instanceFilters.startDateRange;
      const rangeFrom = new Date(fromStr).getTime();
      const rangeTo = new Date(toStr).getTime();
      filteredData = executionData.filter(({ eformInstances }) => {
        if (!eformInstances || eformInstances.length === 0) return false;
        const times = eformInstances
          .map(inst => inst.createdAt || inst.created_at || inst.submittedAt || inst.submitted_at || inst.completedAt || inst.completed_at)
          .filter(Boolean)
          .map(t => new Date(t).getTime())
          .filter(t => !isNaN(t));
        if (times.length === 0) return false;
        const earliest = Math.min(...times);
        return earliest >= rangeFrom && earliest <= rangeTo;
      });
      console.log('[MediaGrouping] eform 日期過濾：', executionData.length, '→', filteredData.length, '個流程實例');
    }

    // 4. 按 PV + eForm 欄位組合分組，並應用流程變量過濾條件
    const groupedMap = new Map();

    filteredData.forEach(({ execution, mediaFiles, processVariables, eformInstances }) => {
      // 只處理有媒體文件的實例
      if (mediaFiles.length === 0) {
        console.log(`[MediaGrouping] 🚫 實例 ${execution.id} 無媒體文件，跳過`);
        return;
      }

      // 檢查是否符合流程變量過濾條件
      if (!matchesAllFilters(processVariables, pvFilters)) {
        console.log(`[MediaGrouping] 🔴 實例 ${execution.id} 被 pvFilters 過濾掉！pvFilters=`, JSON.stringify(pvFilters), '實例PVs=', JSON.stringify(processVariables));
        return;
      }
      console.log(`[MediaGrouping] ✅ 實例 ${execution.id} 通過過濾，媒體 ${mediaFiles.length} 張，PVs=`, JSON.stringify(processVariables));
      // 提取 eForm 欄位值
      const eformValues = hasEformGrouping
        ? extractEformGroupValues(eformInstances, groupingEformFields)
        : {};

      // 生成分組鍵（基於分組 PV + eForm 欄位組合）
      const groupKey = generateGroupKey(processVariables, groupingPvs, eformValues, groupingEformFields);
      console.log(`[MediaGrouping] 實例 ${execution.id} → groupKey="${groupKey}", eformValues=`, JSON.stringify(eformValues));
      // 使用標準化後的狀態進行子分組，這樣所有應該顯示為相同狀態的實例會被分到同一組
      const normalizedStatus = normalizeStatusForDisplay(execution.status || 'Unknown');

      if (!groupedMap.has(groupKey)) {
        groupedMap.set(groupKey, {
          groupKey,
          processVariables: {},
          eformValues: {},  // 該分組的 eForm 欄位代表值
          subGroups: new Map() // 按標準化狀態分組的子組
        });
      }

      const group = groupedMap.get(groupKey);
      
      // 合併流程變量及 eForm 值（使用第一個實例的值作為代表，因為相同分組的值應該相同）
      if (Object.keys(group.processVariables).length === 0) {
        Object.assign(group.processVariables, processVariables);
      }
      if (hasEformGrouping && Object.keys(group.eformValues).length === 0) {
        Object.assign(group.eformValues, eformValues);
      }
      
      // 在該 PV 組合內，按標準化狀態進行子分組
      if (!group.subGroups.has(normalizedStatus)) {
        group.subGroups.set(normalizedStatus, {
          status: normalizedStatus, // 存儲標準化後的狀態用於顯示
          workflowExecution: null,
          executions: [],
          mediaFiles: []
        });
      }
      
      const subGroup = group.subGroups.get(normalizedStatus);
      subGroup.executions.push(execution);
      subGroup.mediaFiles.push(...mediaFiles);
    });

    // 5. 轉換為數組並處理子分組
    const groupedArray = Array.from(groupedMap.values());
    
    groupedArray.forEach(group => {
      // 將子組 Map 轉換為數組
      group.subGroups = Array.from(group.subGroups.values());
      
      // 處理每個子組
      group.subGroups.forEach(subGroup => {
        if (subGroup.executions.length > 0) {
          // 按開始時間排序（最新的在前）
          subGroup.executions.sort((a, b) => {
            const timeA = new Date(a.startedAt || 0).getTime();
            const timeB = new Date(b.startedAt || 0).getTime();
            return timeB - timeA;
          });
          // 將最新的流程實例作為代表（用於顯示）
          subGroup.workflowExecution = subGroup.executions[0];
        }
      });
      
      // 按狀態排序子組（確保一致的順序）
      group.subGroups.sort((a, b) => {
        return (a.status || '').localeCompare(b.status || '');
      });
    });
    
    // 按分組鍵排序
    groupedArray.sort((a, b) => {
      return a.groupKey.localeCompare(b.groupKey);
    });

    const totalSubGroups = groupedArray.reduce((sum, group) => sum + group.subGroups.length, 0);
    console.log('[MediaGrouping] 分組完成，共', groupedArray.length, '個 PV 組合，', totalSubGroups, '個狀態子組');
    groupedArray.forEach(group => {
      group.subGroups.forEach(sg => {
        const ids = sg.executions.map(e => e.id).join(',');
        console.log(`[MediaGrouping] 子組 groupKey="${group.groupKey}" status="${sg.status}" executions(${sg.executions.length})=[${ids}]`);
      });
    });
    return groupedArray;
  } catch (error) {
    console.error('[MediaGrouping] 媒體分組失敗:', error);
    throw error;
  }
};

export default {
  isImageFile,
  fetchWorkflowExecutions,
  fetchExecutionMediaFiles,
  fetchExecutionProcessVariables,
  fetchExecutionEformInstances,
  fetchEformInstanceFullData,
  extractEformGroupValues,
  generateGroupKey,
  groupMediaByExecutionAndPV,
  normalizeStatusForDisplay
};
